import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { GoogleLogo, ArrowRight, Warning } from "@phosphor-icons/react";

const TAGLINES = ["Stock", "Production", "HR", "Purchase", "Job Work", "Dispatch"];
const HERO = "https://images.pexels.com/photos/20856393/pexels-photo-20856393.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=1200";

export default function Login() {
  const { user, loading } = useAuth();
  const [authError, setAuthError] = useState("");
  const [tagIdx, setTagIdx] = useState(0);
  useEffect(() => { const t = setInterval(() => setTagIdx((i) => (i + 1) % TAGLINES.length), 2800); return () => clearInterval(t); }, []);

  useEffect(() => {
    const err = sessionStorage.getItem("auth_error");
    if (err) { setAuthError(err); sessionStorage.removeItem("auth_error"); }
  }, []);

  useEffect(() => {
    if (!loading && user) window.location.href = "/";
  }, [user, loading]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex flex-col justify-center px-8 sm:px-16 py-12 relative grid-texture">
        <div className="max-w-md">
          <div className="flex items-center mb-12">
            <img src="/copprrod-logo.png" alt="COPPRROD – current carriers" className="h-14 w-auto" data-testid="login-logo" />
          </div>

          <h1 className="text-6xl sm:text-7xl font-normal tracking-wide leading-[0.95]" style={{ fontFamily: "'Bebas Neue', 'Nunito Sans', sans-serif" }} data-testid="login-heading">
            The control center of COPPRROD.
          </h1>
          <p className="mt-5 h-6 font-mono text-sm uppercase tracking-[0.08em] text-muted-foreground" aria-live="polite">
            <span key={tagIdx} className="login-tagline inline-block text-primary" data-testid="login-tagline">{TAGLINES[tagIdx]}</span>
          </p>

          {authError && (
            <div className="mt-6 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3" data-testid="auth-error-banner">
              <Warning size={18} weight="bold" className="text-destructive mt-0.5 shrink-0" />
              <p className="text-sm text-destructive">{authError}</p>
            </div>
          )}

          <Button
            data-testid="google-login-button"
            onClick={handleLogin}
            className="mt-10 h-12 px-6 text-sm font-semibold rounded-md group"
          >
            <GoogleLogo size={20} weight="bold" className="mr-2" />
            Continue with Google
            <ArrowRight size={18} weight="bold" className="ml-2 group-hover:translate-x-0.5 transition-transform" />
          </Button>
        </div>
        <div className="absolute bottom-6 left-8 sm:left-16 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground/70" data-testid="login-footer">
          © {new Date().getFullYear()} COPPRROD Industries Pvt Ltd · All rights reserved
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden bg-[#1c1917]" data-testid="login-hero-image">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover login-glow-a" style={{ filter: "sepia(.55) saturate(1.6) hue-rotate(-10deg) brightness(.75)" }} />
        <div className="absolute -inset-[15%] opacity-70 login-glow-b mix-blend-multiply" style={{ background: "radial-gradient(60% 80% at 20% 30%, rgba(180,83,9,.9) 0%, transparent 60%), radial-gradient(50% 60% at 80% 20%, rgba(249,115,22,.8) 0%, transparent 55%), radial-gradient(70% 70% at 70% 85%, rgba(124,45,18,.9) 0%, transparent 60%)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/80 via-transparent to-neutral-950/30" />
      </div>
    </div>
  );
}
